import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EllipsisTooltipText from "./EllipsisTooltipText";
import { StatusColors } from "../../pages-new/Studio/constants";

const COLUMNS = [
  { id: "Step_Number", label: "Step Number", width: "120px" },
  { id: "Page_Description", label: "Page Description", width: "22%" },
  { id: "Action", label: "Action", width: "10%" },
  { id: "TestData", label: "Test Data", width: "22%" },
  { id: "Flow", label: "Flow", width: "10%" },
  { id: "waittime", label: "Wait Time", width: "110px" },
];

const StyledTableContainer = styled(TableContainer)({
  marginTop: 24,
  boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)",
});

const CustomHeaderCell = styled(TableCell)({
  whiteSpace: "nowrap",
  fontWeight: 600,
  color: "#3f4254",
});

const SimilarStepsTable = ({
  data,
  selectedItems = [],
  onSelectAll,
  onSelectItem,
  activeConditions,
}) => {
  const numSelected = selectedItems.length;
  const rowCount = data.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;

  return (
    <StyledTableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                indeterminate={isIndeterminate}
                checked={isAllSelected}
                onChange={onSelectAll}
                inputProps={{ "aria-label": "select all steps" }}
              />
            </TableCell>

            {COLUMNS.map((col) => (
              <CustomHeaderCell key={col.id} sx={{ width: col.width }}>
                {col.label}
              </CustomHeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((item) => {
            const itemId = item.idtest_step ?? item.Step_Number;
            const isSelected = selectedItems.includes(itemId);

            const showTestDataChip =
              !(item.testdata_source === "AsRecorded" && !item.ValDevice) &&
              !(item.Keyword === "Assertion" && item.ValDevice === "OnScreen");

            const chipLabel =
              item.Keyword === "Assertion" && item.ValDevice
                ? item.ValDevice
                : item.testdata_source ?? "Configure";

            return (
              <TableRow
                key={itemId}
                hover
                selected={isSelected}
                sx={{ backgroundColor: StatusColors[item.Flow] || "#ffffff", cursor: "pointer" }}
                onClick={() => onSelectItem(itemId)}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    color="primary"
                    checked={isSelected}
                    onChange={() => onSelectItem(itemId)}
                    inputProps={{ "aria-labelledby": `step-checkbox-${itemId}` }}
                  />
                </TableCell>

                <TableCell id={`step-checkbox-${itemId}`}>{item.Step_Number}</TableCell>

                <TableCell>
                  <EllipsisTooltipText
                    text={item.Page_Description || "-"}
                    maxChars={50}
                    mode="chars"
                  />
                </TableCell>

                <TableCell>
                  <Typography>{item.Action}</Typography>
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EllipsisTooltipText text={item.TestData || "-"} maxChars={50} mode="chars" />
                    {showTestDataChip && (
                      <Chip
                        label={chipLabel}
                        size="small"
                        variant={item.testdata_source ? "outlined" : "filled"}
                        sx={{
                          fontSize: "10px",
                          height: "20px",
                          color: "#547de8",
                          backgroundColor: "#eff4ff",
                          border: "1px solid #547de8",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography>{item.Flow || "-"}</Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{item.waittime || 0}s</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({item.wait || "Before"})
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default SimilarStepsTable;
